import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Star } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews`);
        setReviews(response.data.reviews || []);
      } catch (error) {
        console.error('Failed to fetch reviews:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F2E8] pt-24 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white aspect-[4/3] skeleton"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F2E8] pt-24 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* Header */}
        <div className="mb-12">
          <p className="text-[#4A7C29] text-[10px] font-semibold tracking-[0.2em] mb-2 font-['IBM_Plex_Sans_Condensed'] uppercase">TJGI — FIELD TESTING PROGRAM</p>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-[0.05em] text-[#1A1A1A] font-['IBM_Plex_Sans_Condensed'] uppercase">
            EQUIPMENT EVALUATION REPORTS
          </h1>
          <p className="text-[#5A5A5A] mt-3 max-w-2xl font-['IBM_Plex_Sans']">
            Independent analysis from the ultralight research community. Rigorous methodology. Results may vary. Side effects may include obsessive gram counting.
          </p>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((review) => (
            <Link 
              key={review.id}
              to={`/reviews/${review.id}`}
              className="group bg-white overflow-hidden hover:border-[#4A7C29] transition-all duration-200 border-2 border-[#E0DDD4]"
              data-testid={`review-card-${review.id}`}
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img 
                  src={review.image}
                  alt={review.title}
                  className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-300"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#4A7C29] bg-[#4A7C29]/10 px-2 py-1 font-['IBM_Plex_Sans_Condensed']">
                    {review.category}
                  </span>
                  <span className="text-[9px] text-[#5A5A5A] bg-[#F5F2E8] px-2 py-1 font-['IBM_Plex_Mono'] border border-[#E0DDD4]">
                    {review.weight_saved}
                  </span>
                </div>
                
                <h3 className="font-semibold text-base text-[#1A1A1A] mb-2 group-hover:text-[#4A7C29] transition-colors line-clamp-2 font-['IBM_Plex_Sans_Condensed'] uppercase tracking-wide">
                  {review.title}
                </h3>
                
                <p className="text-[#5A5A5A] text-sm line-clamp-2 mb-4 font-['IBM_Plex_Sans']">
                  {review.excerpt}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`w-3 h-3 ${i < review.rating ? 'text-[#4A7C29] fill-current' : 'text-[#E0DDD4]'}`}
                        />
                      ))}
                    </div>
                    <span className="text-xs text-[#5A5A5A] font-['IBM_Plex_Mono']">{review.rating}/5</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#5A5A5A] group-hover:text-[#4A7C29] group-hover:translate-x-1 transition-all" />
                </div>
                
                <div className="mt-3 pt-3 border-t border-[#E0DDD4] flex items-center justify-between text-xs text-[#5A5A5A] font-['IBM_Plex_Sans']">
                  <span>by {review.author}</span>
                  <span>{review.date}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Reviews;
